import { Map, List, isImmutable } from 'immutable';

import { createController } from '../utils/controller';
import { EMPTY } from '../constants';

import actions from '../actions';
import api from '../utils/api';
import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { shapeFoldersForApp, shapeVideosForApp } from '../utils/studio/shapeForApp';
import { IMAGES_PER_PAGE, ORDER_BY } from '../constants/studio';
import { getSize } from '../utils/iterable/getSize';
import { getFilters } from '../utils/studio/filters';
import { downloadFile } from '../utils/files';
import { shapeVideoFileForEditor } from '../utils/photoEditor/state';
import { increase } from '../utils/math';

const videosController = createController();
const foldersController = createController();

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    videosController,
    foldersController,
  ].forEach(abort);
}

function getShopParams(state) {
  const channel = state.getIn(['studio', 'channel']);
  const db = state.getIn(['studio', 'db']);
  const shopId = state.getIn(['studio', 'shopId']);
  return { channel, db, shopId };
}

function* bootstrap(reduction, { channel, db, shopId }) {
  abortAll();

  let state = Map({ channel, db, shopId });

  yield function effect(dispatch) {
    dispatch(actions.studio.video.getFolders());
    dispatch(actions.studio.video.getItems());
  };

  if (reduction.hasIn(['video', 'uploading'])) {
    state = state.set('uploading', reduction.getIn(['video', 'uploading']));
  }

  return reduction.set('video', state);
}

function* cleanUp(reduction) {
  abortAll();

  let state = EMPTY.MAP;

  if (reduction.hasIn(['video', 'uploading'])) {
    state = state.set('uploading', reduction.getIn(['video', 'uploading']));
  }

  return reduction.set('video', state);
}

function* getFolders(reduction) {
  const { db, shopId } = getShopParams(reduction);
  const { signal } = foldersController.start();

  yield function effect(dispatch) {
    api.studio.video.getFolders({ db, shopId, signal })
      .then(
        (response) => dispatch(actions.studio.video.getFolders.fulfilled(response)),
        (error) => dispatch(actions.studio.video.getFolders.rejected({ error, signal })),
      );
  };

  return reduction;
}

function* getFoldersRejected(reduction, { error, signal }) {
  if (signal.aborted) return reduction;

  abort(foldersController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction
    .setIn(['video', 'folders'], Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }))
    .setIn(['video', 'loaded'], reduction.hasIn(['video', 'items']));
}

function* getFoldersFulfilled(reduction, response) {
  abort(foldersController);

  let state = reduction.get('video');
  const allVideos = Map({ count: parseInt(response.video_count) || 0, name: 'All videos' });
  const folders = response.folders.reduce(
    shapeFoldersForApp,
    state
      .get('folders', Map({ selected: null }))
      .set('byId', EMPTY.MAP.set(null, allVideos))
      .set('ids', List([null]))
  );

  state = state
    .set('folders', folders)
    .set('loaded', state.has('items'));

  if (state.get('loaded') && !state.getIn(['request', 'folder'])) {
    state = state.setIn(['request', 'folder'], folders.getIn(['byId', folders.get('selected')]));
  }

  return reduction.set('video', state);
}

function* getItems(reduction, payload = {}) {
  const { signal } = videosController.start();
  const { db, shopId } = getShopParams(reduction);
  let state = reduction.get('video').setIn(['request', 'processing'], true);
  const {
    folder = state.getIn(['request', 'folder', 'uuid'], null),
    order = { by: state.getIn(['order', 'by'], null), type: state.getIn(['order', 'type'], false) },
  } = payload;

  let {
    clear = false,
    page = state.getIn(['request', 'page'], 0),
    query = state.getIn(['request', 'query'], ''),
  } = payload;

  if (state.has('folders') && folder !== state.getIn(['folders', 'selected'])) {
    state = state.setIn(['folders', 'selected'], folder);
  }

  if (folder !== state.getIn(['request', 'folder', 'uuid'], null)) {
    clear = true;
    query = '';
  } else if (query !== state.getIn(['request', 'query'], '')) {
    clear = true;
    state = state.set('query', query);
  } else if (order.by !== state.getIn(['order', 'by'], null) || order.type !== state.getIn(['order', 'type'], false)) {
    clear = true;

    if (order.by) {
      state = state
        .setIn(['order', 'by'], order.by)
        .setIn(['order', 'type'], order.type);
    } else {
      order.by = undefined;
      order.type = undefined;
      state = state
        .deleteIn(['order', 'by'])
        .deleteIn(['order', 'type']);
    }
  }

  if (!order.by) {
    order.by = ORDER_BY.LISTINGS_CONNECTED;
    order.type = false;
    state = state
      .setIn(['order', 'by'], order.by)
      .setIn(['order', 'type'], order.type);
  }

  if (clear) {
    state = state.setIn(['items', 'loading'], true);
    page = 0;
  }

  const request = Map({
    folder: state.getIn(['folders', 'byId', folder]),
    order: Map(order),
    page,
    query,
  });

  yield function effect(dispatch) {
    api.studio.video.getVideos({ db, filters: getFilters({ order, page, query }), folder, shopId, signal })
      .then(
        (response) => dispatch(actions.studio.video.getItems.fulfilled({ clear, request, response })),
        (error) => dispatch(actions.studio.video.getItems.rejected({ error, request, signal })),
      );
  };

  return reduction.set('video', state);
}

function* getItemsRejected(reduction, { error, request, signal }) {
  if (signal.aborted) return reduction;

  abort(videosController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  const state = reduction
    .get('video')
    .set('items', Map({ byId: EMPTY.MAP, ids: EMPTY.LIST, total: 0 }))
    .set('request', request);

  return reduction.set('video', state.set('loaded', state.has('folders')));
}

function* getItemsFulfilled(reduction, { clear, request, response }) {
  abort(videosController);

  let state = reduction.get('video');
  let videos = state
    .get('items', Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }))
    .delete('loading')
    .delete('processing')
    .set('total', parseInt(response.count));

  if (clear) {
    videos = videos
      .delete('selected')
      .set('byId', EMPTY.MAP)
      .set('ids', EMPTY.LIST);
  }

  state = state
    .set('items', response.videos.reduce(shapeVideosForApp, videos))
    .set('loaded', state.has('folders'))
    .set('request', request);

  if ((request.get('page') + 1) * IMAGES_PER_PAGE < videos.get('total')) {
    state = state.setIn(['request', 'hasMore'], true);
  }

  if (state.get('loaded') && !request.get('folder') && getSize(state.getIn(['folders', 'byId']))) {
    state = state.setIn(['request', 'folder'],
      state.getIn(['folders', 'byId', state.getIn(['folders', 'selected'], null)])
    );
  }

  return reduction.set('video', state);
}

function* createFolder(reduction, { callback, name }) {
  const { db, shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    api.studio.video.createFolder({ db, name, shopId })
      .then(
        ({ uuid }) => dispatch(actions.studio.video.createFolder.fulfilled({ callback, name, uuid })),
        (error) => dispatch(actions.studio.video.createFolder.rejected(error)),
      );
  };

  return reduction;
}

function* createFolderRejected(reduction, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.CREATE_FOLDER,
      })
    );
  };

  return reduction;
}

function* createFolderFulfilled(reduction, { callback, name, uuid }) {
  yield function effect(dispatch) {
    if (typeof callback === 'function') {
      callback(uuid);
    } else {
      dispatch(
        actions.notifications.add({
          type: NOTIFICATION.SUCCESS,
          message: MESSAGE.SUCCESS.CREATE_FOLDER,
        })
      );
    }

    dispatch(actions.data.setData({ path: 'lastInteraction' }));
    dispatch(actions.studio.video.getFolders());
    dispatch(actions.studio.video.getItems({ folder: uuid }));
  };

  function updateById(byId) {
    return byId.set(uuid, Map({ count: 0, name, uuid }));
  }

  function updateIds(ids) {
    return ids.push(uuid);
  }

  return reduction
    .updateIn(['video', 'folders', 'byId'], updateById)
    .updateIn(['video', 'folders', 'ids'], updateIds);
}

function* deleteFolder(reduction, folder) {
  const { db, shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    api.studio.video.deleteFolder({ db, folder, shopId })
      .then(
        () => dispatch(actions.studio.video.deleteFolder.fulfilled(folder)),
        (error) => dispatch(actions.studio.video.deleteFolder.rejected(error)),
      );
  };

  return reduction;
}

function* deleteFolderRejected(reduction, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.DELETE_FOLDER,
      })
    );
  };

  return reduction;
}

function* deleteFolderFulfilled(reduction, uuid) {
  yield function effect(dispatch) {
    dispatch(actions.data.setData({ path: 'lastInteraction' }));
    dispatch(actions.studio.video.getFolders());
    dispatch(actions.studio.video.getItems({ folder: null }));
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: MESSAGE.SUCCESS.DELETE_FOLDER,
      })
    );
  };

  function updateIds(ids) {
    return ids.filter(function filterIds(item) {
      return item !== uuid;
    });
  }

  return reduction
    .deleteIn(['video', 'folders', 'byId', uuid])
    .updateIn(['video', 'folders', 'ids'], updateIds);
}

function* renameFolder(reduction, { folder, name }) {
  const { db, shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    api.studio.video.renameFolder({ db, folder, name, shopId })
      .then(
        () => dispatch(actions.studio.video.renameFolder.fulfilled({ name, uuid: folder })),
        (error) => dispatch(actions.studio.video.renameFolder.rejected(error)),
      );
  };

  return reduction;
}

function* renameFolderRejected(reduction, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.RENAME_FOLDER,
      })
    );
  };

  return reduction;
}

function* renameFolderFulfilled(reduction, { name, uuid }) {
  yield function effect(dispatch) {
    dispatch(actions.data.setData({ path: 'lastInteraction' }));
    dispatch(actions.studio.video.getFolders());
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: MESSAGE.SUCCESS.RENAME_FOLDER,
      })
    );
  };

  function updateFolder(folder) {
    return folder.set('name', name);
  }

  return reduction.updateIn(['video', 'folders', 'byId', uuid], updateFolder);
}

function* setValue(reduction, { path, value }) {
  if (!path) return reduction;

  const fullPath = Array.isArray(path)
    ? ['video', ...path]
    : ['video', path];

  return value === undefined
    ? reduction.deleteIn(fullPath)
    : reduction.setIn(fullPath, value);
}

function* toggleVideoState(reduction, { videos, state: path, value }) {
  function reduceVideoIds(result, uuid) {
    switch (value) {
      case true: {
        return result.add(uuid);
      }

      case false: {
        return result.remove(uuid);
      }

      default: {
        return result.has(uuid)
          ? result.remove(uuid)
          : result.add(uuid);
      }
    }
  }

  const fullPath = ['video', 'items', path];
  const state = typeof videos === 'string'
    ? reduceVideoIds(reduction.getIn(fullPath, EMPTY.SET), videos)
    : Array.isArray(videos) || isImmutable(videos)
      ? videos.reduce(reduceVideoIds, reduction.getIn(fullPath, EMPTY.SET))
      : null;

  return getSize(state)
    ? reduction.setIn(fullPath, state)
    : reduction.deleteIn(fullPath);
}

function* deleteItems(reduction, { uuids }) {
  const { db, shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    dispatch(
      actions.studio.video.toggleVideoState({
        videos: uuids,
        state: 'processing',
        value: true,
      })
    );

    api.studio.video.deleteVideos({ db, shopId, uuids })
      .then(
        () => dispatch(actions.studio.video.deleteItems.fulfilled(shopId)),
        (error) => dispatch(actions.studio.video.deleteItems.rejected({ error, shopId })),
      );
  };

  return reduction;
}

function* deleteItemsRejected(reduction, { error, shopId }) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.DELETE_VIDEOS,
        type: NOTIFICATION.ERROR,
      })
    );

    if (shopId === getShopParams(reduction).shopId) {
      dispatch(actions.data.setData({ path: 'lastInteraction' }));
      dispatch(actions.studio.video.getFolders());
      dispatch(actions.studio.video.getItems({ clear: true }));
    }
  };

  return reduction;
}

function* deleteItemsFulfilled(reduction, shopId) {
  yield function effect(dispatch) {
    dispatch(
      actions.notifications.add({
        message: MESSAGE.SUCCESS.DELETE_VIDEOS,
        type: NOTIFICATION.SUCCESS,
      })
    );

    if (shopId === getShopParams(reduction).shopId) {
      dispatch(actions.data.setData({ path: 'lastInteraction' }));
      dispatch(actions.studio.video.getFolders());
      dispatch(actions.studio.video.getItems({ clear: true }));
    }
  };

  return reduction;
}

function* downloadItems(reduction, { name, uuids }) {
  const { db, shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    dispatch(
      actions.studio.video.toggleVideoState({
        videos: uuids,
        state: 'processing',
        value: true,
      })
    );

    api.studio.video.downloadVideos({ db, shopId, uuids })
      .then((blob) => new Promise(function executor(resolve, reject) {
        const reader = new FileReader();

        reader.addEventListener('loadend', function onReaderLoad() {
          try {
            downloadFile({ data: reader.result, name });
            resolve();
          } catch (error) {
            reject(error);
          }
        });

        reader.readAsDataURL(blob);
      }))
      .then(
        () => dispatch(actions.studio.video.downloadItems.fulfilled({ shopId, uuids })),
        (error) => dispatch(actions.studio.video.downloadItems.rejected({ error, shopId, uuids })),
      );
  };

  return reduction;
}

function* downloadItemsRejected(reduction, { error, shopId, uuids }) {
  yield function effect(dispatch) {
    console.error(error);

    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.DOWNLOAD_VIDEOS,
        type: NOTIFICATION.ERROR,
      })
    );

    if (shopId === getShopParams(reduction).shopId) {
      dispatch(
        actions.studio.video.toggleVideoState({
          videos: uuids,
          state: 'processing',
          value: false,
        })
      );
    }
  };

  return reduction;
}

function* downloadItemsFulfilled(reduction, { shopId, uuids }) {
  if (shopId === getShopParams(reduction).shopId) {
    yield function effect(dispatch) {
      dispatch(
        actions.studio.video.toggleVideoState({
          videos: uuids,
          state: 'processing',
          value: false,
        })
      );
    };
  }

  return reduction;
}

function* uploadFiles(reduction, { files }) {
  const current = getShopParams(reduction);
  let state = reduction.get('video');

  if (getSize(files) === 0) {
    const folder = state.getIn(['uploading', 'folder']);
    const count = state.getIn(['uploading', 'count']);
    const shopId = state.getIn(['uploading', 'shopId']);
    const total = state.getIn(['uploading', 'total']);

    if (total) {
      yield function effect(dispatch) {
        if (!count) {
          dispatch(
            actions.notifications.add({
              message: MESSAGE.FAIL.UPLOAD_FILES,
              type: NOTIFICATION.ERROR,
            })
          );

          return;
        }

        dispatch(
          actions.notifications.add({
            message: count === total
              ? MESSAGE.SUCCESS.UPLOAD_FILES
              : `${count} of ${total} files uploaded`,
            type: NOTIFICATION.SUCCESS,
          })
        );

        if (shopId === current.shopId) {
          dispatch(actions.studio.video.getFolders());

          if (
            !state.getIn(['request', 'processing']) &&
            folder === state.getIn(['request', 'folder', 'uuid'])
          ) {
            dispatch(actions.data.setData({ path: 'lastInteraction' }));
            dispatch(actions.studio.video.getItems({ clear: true }));
          }
        }
      };
    }

    return reduction.set('video', state.delete('uploading'));
  }

  if (!state.get('uploading')) {
    const { channel, db, shopId } = current;
    const folder = state.getIn(['request', 'folder', 'uuid']);
    const total = getSize(files);
    const uploading = Map({
      channel,
      count: 0,
      db,
      folder,
      images: Map({ byId: EMPTY.MAP, ids: EMPTY.LIST }),
      shopId,
      total,
    });

    state = state.setIn(['uploading'], uploading);
  }

  yield function effect(dispatch) {
    const file = files.first();
    const channel = state.getIn(['uploading', 'channel']);
    shapeVideoFileForEditor({ channel, file })
      .then(
        (video) => dispatch(actions.studio.video.uploadFiles.fulfilled({ files, video })),
        (error) => dispatch(actions.studio.video.uploadFiles.rejected({ error, files })),
      );
  };

  return reduction.set('video', state);
}

function* uploadFilesRejected(reduction, { error, files, video }) {
  let state = reduction.get('video');

  yield function effect(dispatch) {
    console.error(error);
    dispatch(actions.studio.video.uploadFiles({ files: files.shift() }));
  };

  function updateIds(ids) {
    return ids.filter(function filterIds(id) {
      return id !== video.id;
    });
  }

  if (video) {
    state = state
      .deleteIn(['uploading', 'items', 'byId', video.id])
      .updateIn(['uploading', 'items', 'ids'], updateIds);
  }

  return reduction.set('video', state);
}

function* uploadFilesFulfilled(reduction, { files, video, uploaded }) {
  const current = getShopParams(reduction);
  let state = reduction.get('video');

  if (uploaded) {
    const folder = state.getIn(['uploading', 'folder']);
    const shopId = state.getIn(['uploading', 'shopId']);

    if (shopId === current.shopId) {
      if (folder) {
        state = state
          .updateIn(['folders', 'byId', null, 'count'], increase)
          .updateIn(['folders', 'byId', folder, 'count'], increase);
      } else {
        state = state.updateIn(['folders', 'byId', null, 'count'], increase);
      }

      if (
        !state.getIn(['request', 'processing']) &&
        folder === state.getIn(['request', 'folder', 'uuid'])
      ) {
        const image = state
          .getIn(['uploading', 'items', 'byId', video.id])
          .set('url', uploaded.url)
          .set('uuid', uploaded.uuid);

        state = state
          .updateIn(['items', 'total'], increase)
          .setIn(['items', 'byId', uploaded.uuid], image)
          .updateIn(['items', 'ids'], function updateIds(ids) {
            return ids.unshift(uploaded.uuid);
          });
      }
    }

    state = state
      .updateIn(['uploading', 'count'], increase)
      .deleteIn(['uploading', 'items', 'byId', video.id])
      .updateIn(['uploading', 'items', 'ids'], function updateIds(ids) {
        return ids.filter(function filterIds(id) {
          return id !== video.id;
        });
      });

    yield function effect(dispatch) {
      dispatch(actions.studio.video.uploadFiles({ files: files.shift() }));
    };
  } else {
    const { data, format, id, mime, name, url: preview } = video;
    state = state
      .updateIn(['uploading', 'items', 'byId'], function updateById(byId = EMPTY.MAP) {
        return byId.set(id,
          Map({
            fileName: name,
            format,
            mime,
            url: preview,
            uuid: id,
          })
        );
      })
      .updateIn(['uploading', 'items', 'ids'], function update(ids = EMPTY.LIST) {
        return ids.push(id);
      });

    yield function effect(dispatch) {
      const db = state.getIn(['uploading', 'db']);
      const folder = state.getIn(['uploading', 'folder']);
      const shopId = state.getIn(['uploading', 'shopId']);

      function onFail(error) {
        dispatch(actions.studio.video.uploadFiles.rejected({ error, files, video }));
      }

      function onUploaded({ url }) {
        function onSaved({ fileUuid: uuid }) {
          dispatch(
            actions.studio.video.uploadFiles.fulfilled({
              files,
              video,
              uploaded: {
                url,
                uuid,
              },
            })
          );
        }

        api.studio.video.saveVideo({ db, folder, format, name, shopId, url })
          .then(onSaved)
          .catch(onFail);
      }

      api.studio.video.uploadToS3({ data, db, mime, format, shopId })
        .then(onUploaded)
        .catch(onFail);
    };
  }

  return reduction.set('video', state);
}

function* moveItems(reduction, { source, target, uuids }) {
  if (source === target || !Array.isArray(uuids) || !getSize(uuids)) return reduction;

  const { db, shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    api.studio.video.moveVideos({ db, shopId, source, target, uuids })
      .then(
        () => dispatch(actions.studio.video.moveItems.fulfilled({ target, uuids })),
        (error) => dispatch(actions.studio.video.moveItems.rejected(error)),
      );
  };

  return reduction;
}

function* moveItemsRejected(reduction, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.MOVE_VIDEOS,
      })
    );
  };

  return reduction;
}

function* moveItemsFulfilled(reduction, { target, uuids }) {
  const { shopId } = getShopParams(reduction);

  yield function effect(dispatch) {
    dispatch(actions.data.setData({ path: 'lastInteraction' }));
    dispatch(actions.studio.video.getFolders());

    if (reduction.getIn(['video', 'folders', 'selected']) || !target) {
      dispatch(actions.studio.video.getItems({ clear: true }));
    }

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.VIDEOS_MOVED,
        folder: Map({
          title: reduction.getIn(['video', 'folders', 'byId', target, 'name']),
          uuid: target,
        }),
        shopId,
      })
    );
  };

  function reduceSelectedImages(result, uuid) {
    return result.remove(uuid);
  }

  const path = ['video', 'items', 'selected'];
  const selectedImages = uuids.reduce(reduceSelectedImages, reduction.getIn(path, EMPTY.SET));

  return reduction.setIn(path, selectedImages);
}

const video = {
  [actions.studio.video.bootstrap]: bootstrap,
  [actions.studio.video.cleanUp]: cleanUp,
  [actions.studio.video.getFolders]: getFolders,
  [actions.studio.video.getFolders.fulfilled]: getFoldersFulfilled,
  [actions.studio.video.getFolders.rejected]: getFoldersRejected,
  [actions.studio.video.createFolder]: createFolder,
  [actions.studio.video.createFolder.fulfilled]: createFolderFulfilled,
  [actions.studio.video.createFolder.rejected]: createFolderRejected,
  [actions.studio.video.deleteFolder]: deleteFolder,
  [actions.studio.video.deleteFolder.fulfilled]: deleteFolderFulfilled,
  [actions.studio.video.deleteFolder.rejected]: deleteFolderRejected,
  [actions.studio.video.renameFolder]: renameFolder,
  [actions.studio.video.renameFolder.fulfilled]: renameFolderFulfilled,
  [actions.studio.video.renameFolder.rejected]: renameFolderRejected,
  [actions.studio.video.getItems]: getItems,
  [actions.studio.video.getItems.fulfilled]: getItemsFulfilled,
  [actions.studio.video.getItems.rejected]: getItemsRejected,
  [actions.studio.video.setValue]: setValue,
  [actions.studio.video.toggleVideoState]: toggleVideoState,
  [actions.studio.video.deleteItems]: deleteItems,
  [actions.studio.video.deleteItems.fulfilled]: deleteItemsFulfilled,
  [actions.studio.video.deleteItems.rejected]: deleteItemsRejected,
  [actions.studio.video.downloadItems]: downloadItems,
  [actions.studio.video.downloadItems.fulfilled]: downloadItemsFulfilled,
  [actions.studio.video.downloadItems.rejected]: downloadItemsRejected,
  [actions.studio.video.moveItems]: moveItems,
  [actions.studio.video.moveItems.fulfilled]: moveItemsFulfilled,
  [actions.studio.video.moveItems.rejected]: moveItemsRejected,
  [actions.studio.video.uploadFiles]: uploadFiles,
  [actions.studio.video.uploadFiles.fulfilled]: uploadFilesFulfilled,
  [actions.studio.video.uploadFiles.rejected]: uploadFilesRejected,
};

export default video;
